/**
 * Shared mutation hook for recording a membership/session/class payment (Task 0.7).
 * Reused by Members→Payments and any other surface needing a fiscal payment flow.
 */
import { newIdempotencyKey, recordPayment, type RecordPaymentResult } from '@gym/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { toast } from '../../components/ui/Toaster.js';
import { usePosContext } from '../../lib/pos.js';
import { supabase } from '../../lib/supabase.js';

export interface RecordPaymentArgs {
  memberId: string | null;
  kind: 'membership' | 'pt_session' | 'class_pack' | 'day_pass' | 'other';
  amountMinor: number;
  fiscalize: boolean;
  lineItemName: string;
  /**
   * Optional plan id. When passed with kind==='membership' + a real member_id, the
   * Edge Function creates/renews the membership AND records income in one call.
   */
  membershipPlanId?: string;
}

/**
 * Returns a TanStack mutation that calls recordPayment with a single line-item and
 * single cash tender.
 *
 * Readiness rule:
 * - fiscalize=true  → requires pos.ready (location + device + staff all present)
 * - fiscalize=false → requires only location + staff (missing device is OK)
 */
export function useRecordPayment(onDone?: (r: RecordPaymentResult) => void) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const pos = usePosContext();

  return useMutation<RecordPaymentResult, Error, RecordPaymentArgs>({
    mutationFn: async (args: RecordPaymentArgs): Promise<RecordPaymentResult> => {
      const { memberId, kind, amountMinor, fiscalize, lineItemName, membershipPlanId } = args;

      // Readiness check: device only required when fiscalizing
      const missingCritical = fiscalize ? pos.missing : pos.missing.filter((m) => m !== 'device');

      if (missingCritical.length > 0) {
        throw new Error(t('billing.posNotReady'));
      }

      return recordPayment(
        supabase,
        {
          tenant_id: pos.tenantId,
          location_id: pos.locationId!,
          member_id: memberId,
          kind,
          ...(membershipPlanId ? { membership_plan_id: membershipPlanId } : {}),
          tenders: [{ method: 'cash', amount: amountMinor }],
          gross_amount: amountMinor,
          recorded_by_staff_id: pos.staffId!,
          issued_at: new Date().toISOString(),
          ...(fiscalize && pos.fiscalDeviceId ? { fiscal_device_id: pos.fiscalDeviceId } : {}),
          fiscalize,
          line_items: [
            {
              name: lineItemName,
              quantity: 1,
              unit_price: amountMinor,
              tax_rate_code: 'A',
              total: amountMinor,
            },
          ],
        },
        newIdempotencyKey(),
      );
    },
    onSuccess: (res) => {
      toast.success(t('billing.paymentRecorded'));
      void qc.invalidateQueries({ queryKey: ['revenue'] });
      void qc.invalidateQueries({ queryKey: ['member_payments'] });
      onDone?.(res);
    },
    onError: (e: Error) => {
      toast.error(t('billing.paymentFailed'), e.message);
    },
  });
}
